import React from 'react';

/**
 * Public SMS Opt-In Disclosure page — https://nuvesystem.com/sms-opt-in
 *
 * Purpose: provides carriers and The Campaign Registry (TCR) reviewers with a
 * publicly verifiable record of how end-users consent to receive SMS messages
 * from NuVe System. The consent cards below are exact reproductions of the
 * screens shown to customers on the in-store tablet and via the secure
 * activation link on the customer's own phone.
 */

class SmsOptIn extends React.Component {
  handleBack = () => {
    if (this.props.history) {
      this.props.history.goBack();
    } else {
      window.history.back();
    }
  };

  render() {
    return (
      <div style={containerStyle}>
        {/* --- FIXED APP TOOLBAR --- */}
        <div style={toolbarStyle}>
          <button onClick={this.handleBack} style={iconButtonStyle}>
            ←
          </button>
          <div style={toolbarTitleStyle}>SMS Opt-In Disclosure</div>
          <div style={{ width: '40px' }}></div>
        </div>

        <div style={cardStyle}>
          {/* Header */}
          <header style={headerStyle}>
            <h1 style={titleStyle}>
              NuVe System — SMS Program &amp; Opt-In Disclosure
            </h1>
            <p style={{ ...dateStyle, fontStyle: 'italic' }}>
              Last Updated: June 12, 2026
            </p>
          </header>

          <div style={contentStyle}>
            {/* 1. Program overview */}
            <section style={sectionStyle}>
              <h2 style={headingStyle}>1. About This Page</h2>
              <p>
                NuVe System is a membership and loyalty platform used by
                independently operated business locations. This page documents
                how end-users provide consent to receive text messages from NuVe
                System, and reproduces the exact consent screens presented to
                customers. It is published for transparency and for verification
                by wireless carriers and The Campaign Registry.
              </p>
              <p>NuVe System sends two categories of SMS messages:</p>
              <ul style={listStyle}>
                <li>
                  <strong>Phone Verification (required):</strong> one-time
                  verification codes (OTP) used to confirm the customer's
                  identity and provide access to membership features.
                </li>
                <li>
                  <strong>Member Benefits &amp; Promotions (optional):</strong>{' '}
                  recurring promotional offers, loyalty rewards, special event
                  announcements, and business updates — sent{' '}
                  <strong>only</strong> to customers who separately and
                  expressly opt in.
                </li>
              </ul>
              <div style={importantNoticeStyle}>
                <strong>
                  Message frequency varies. Message and data rates may apply.
                </strong>{' '}
                Reply <strong>STOP</strong> to opt out of promotional messages
                at any time. Reply <strong>HELP</strong> for help, or contact
                info@nuvesystem.com. Consent to promotional messages is{' '}
                <strong>not</strong> a condition of membership or of using NuVe
                System. Mobile phone numbers and SMS opt-in data are never
                shared with, sold to, or rented to third parties or affiliates
                for marketing purposes.
              </div>
            </section>

            {/* 2. How opt-in happens */}
            <section style={sectionStyle}>
              <h2 style={headingStyle}>2. How End-Users Opt In</h2>
              <p>
                Consent is captured digitally, directly from the customer, in
                one of two ways:
              </p>
              <ul style={listStyle}>
                <li>
                  <strong>At the location (existing members):</strong> after
                  entering their phone number on the check-in tablet, the
                  customer is shown the consent screen reproduced below. The
                  customer must accept the required Phone Verification consent
                  to continue; the Member Benefits &amp; Promotions consent is
                  optional and unchecked by default.
                </li>
                <li>
                  <strong>On the customer's own phone (new members):</strong>{' '}
                  during enrollment, the customer receives a secure one-time
                  activation link by SMS and completes the same consent screen
                  on their own device before their membership is activated.
                </li>
              </ul>
              <p>
                Each acceptance is recorded with a UTC timestamp, the location,
                and the consent given, and the record is stored immutably as
                proof of opt-in. Promotional messages are sent only to phone
                numbers whose record shows an affirmative marketing opt-in.
              </p>
            </section>

            {/* 3. Consent screen reproduction */}
            <section style={sectionStyle}>
              <h2 style={headingStyle}>
                3. The Consent Screen (Exact Reproduction)
              </h2>
              <p>
                The following is the consent screen shown to every customer. The
                first card is required for membership verification; the second
                card is the optional marketing opt-in.
              </p>

              {/* --- Tablet frame --- */}
              <div style={tabletFrameStyle}>
                <div style={lockRowStyle}>
                  <span style={lockIconStyle} aria-hidden="true">
                    🔒
                  </span>
                  <span style={lockTextStyle}>
                    Please review and accept to continue
                  </span>
                </div>

                {/* Card 1 — Phone Verification (required) */}
                <div style={consentCardStyle}>
                  <div style={consentHeaderStyle}>
                    <span style={cardIconStyle} aria-hidden="true">
                      📱
                    </span>
                    <span style={consentTitleStyle}>Phone Verification</span>
                    <span style={chevronStyle} aria-hidden="true">
                      ⌃
                    </span>
                  </div>
                  <div style={consentBodyStyle}>
                    <p style={consentSubheadStyle}>
                      Why We Need Your Phone Number
                    </p>
                    <p style={consentTextStyle}>
                      Your phone number serves as your unique membership
                      identifier within NuVe System.
                    </p>
                    <p style={consentTextStyle}>
                      When checking in, you may receive a one-time verification
                      code (OTP) via SMS to confirm your identity and access
                      your membership account. Message and data rates may apply.
                    </p>
                    <p style={consentTextStyle}>
                      If you choose not to provide a phone number, please speak
                      with the location manager. A phone number is required to
                      access membership features within the system.
                    </p>
                    <p style={consentTextStyle}>
                      By continuing, you acknowledge and agree to the use of
                      your phone number for identity verification, account
                      access, and membership management purposes.
                    </p>
                    <div style={checkboxRowStyle}>
                      <span style={checkboxCheckedStyle} aria-hidden="true">
                        ✓
                      </span>
                      <span style={checkboxLabelStyle}>
                        I understand my phone number will be used for membership
                        verification, account access, and membership management
                        within NuVe System.
                      </span>
                    </div>
                  </div>
                </div>

                {/* Card 2 — Member Benefits & Promotions (optional) */}
                <div style={consentCardOptionalStyle}>
                  <div style={consentHeaderStyle}>
                    <span style={cardIconStyle} aria-hidden="true">
                      🎁
                    </span>
                    <span style={consentTitleStyle}>
                      Member Benefits &amp; Promotions
                    </span>
                    <span style={chevronStyle} aria-hidden="true">
                      ⌃
                    </span>
                  </div>
                  <div style={consentBodyStyle}>
                    <p style={consentTextStyle}>
                      You may receive promotional offers, special event
                      announcements, loyalty program updates, membership
                      rewards, exclusive member benefits, business updates, and
                      SMS notifications. Message frequency varies.
                    </p>
                    <p style={consentTextStyle}>
                      Message and data rates may apply.
                    </p>
                    <p style={consentTextStyle}>
                      Your consent to receive promotional messages is not
                      required to use NuVe System.
                    </p>
                    <p style={consentTextStyle}>
                      You may opt out at any time by texting STOP to any
                      promotional SMS message or by updating your communication
                      preferences with the business location. Text HELP for
                      help.
                    </p>
                    <div style={checkboxRowStyle}>
                      <span
                        style={checkboxEmptyStyle}
                        aria-hidden="true"
                      ></span>
                      <span style={checkboxLabelStyle}>
                        I agree to receive promotional messages, special offers,
                        loyalty rewards, membership benefits, and business
                        updates via SMS. I understand that I can opt out at any
                        time by texting STOP.
                      </span>
                    </div>
                    <p style={optionalNoteStyle}>
                      This consent is optional and unchecked by default. The
                      customer can continue without accepting it.
                    </p>
                  </div>
                </div>

                <div style={acceptButtonStyle}>ACCEPT &amp; CONTINUE</div>
              </div>
            </section>

            {/* 4. Sample messages */}
            <section style={sectionStyle}>
              <h2 style={headingStyle}>4. Example Messages</h2>
              <p>
                <strong>Verification (required consent):</strong>
              </p>
              <div style={smsBubbleStyle}>
                Your NuVe verification code is: 123456. This code expires in 5
                minutes.
              </div>
              <p>
                <strong>Promotional (optional consent only):</strong>
              </p>
              <div style={smsBubbleStyle}>
                NuVe System: Bonus play points are waiting for you this weekend!
                Reply STOP to opt out, HELP for help.
              </div>
            </section>

            {/* 5. Opt-out & help */}
            <section style={sectionStyle}>
              <h2 style={headingStyle}>5. Opt-Out and Support</h2>
              <ul style={listStyle}>
                <li>
                  <strong>STOP:</strong> Reply STOP to any promotional message
                  to cancel promotional messages. You will receive one
                  confirmation message and no further promotional messages.
                </li>
                <li>
                  <strong>HELP:</strong> Reply HELP to any message, or contact
                  info@nuvesystem.com for assistance.
                </li>
                <li>
                  Customers may also update their communication preferences with
                  staff at the business location.
                </li>
                <li>
                  Wireless carriers are not liable for delayed or undelivered
                  messages.
                </li>
              </ul>
            </section>

            {/* 6. Links */}
            <section style={sectionStyle}>
              <h2 style={headingStyle}>6. Policies</h2>
              <p>
                <a href="/privacy-policy" style={linkStyle}>
                  Privacy Policy
                </a>
                {' · '}
                <a href="/terms-and-conditions" style={linkStyle}>
                  Terms and Conditions
                </a>
              </p>
              <div style={contactBoxStyle}>
                NuVe System
                <br />
                info@nuvesystem.com
              </div>
            </section>
          </div>
        </div>
      </div>
    );
  }
}

// ===== Page styles (consistent with PrivacyPolicy / TermsAndConditions) =====

const dateStyle = {
  color: '#777',
  fontSize: '0.95rem',
  fontWeight: '400',
};

const contentStyle = {
  textAlign: 'left',
};

const sectionStyle = {
  marginBottom: '35px',
};

const headingStyle = {
  color: '#001665',
  fontSize: '1.3rem',
  marginBottom: '15px',
  fontWeight: '700',
};

const listStyle = {
  paddingLeft: '25px',
  marginBottom: '15px',
};

const importantNoticeStyle = {
  backgroundColor: '#f8f9ff',
  borderLeft: '4px solid #001665',
  padding: '15px 20px',
  marginTop: '15px',
  fontSize: '0.95rem',
  fontStyle: 'italic',
};

const contactBoxStyle = {
  backgroundColor: '#f4f4f4',
  padding: '20px',
  borderRadius: '8px',
  marginTop: '10px',
  display: 'inline-block',
  minWidth: '250px',
};

const linkStyle = {
  color: '#1a237e',
  fontWeight: '600',
};

const toolbarStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  height: '60px',
  backgroundColor: '#001665',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0 15px',
  zIndex: 1000,
  boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
};

const iconButtonStyle = {
  backgroundColor: 'transparent',
  color: 'white',
  border: 'none',
  fontSize: '24px',
  cursor: 'pointer',
  padding: '10px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const toolbarTitleStyle = {
  color: 'white',
  fontSize: '18px',
  fontWeight: '600',
};

const containerStyle = {
  backgroundColor: '#ffffff',
  minHeight: '100vh',
  paddingTop: '80px',
  paddingBottom: '40px',
  paddingLeft: '15px',
  paddingRight: '15px',
  display: 'flex',
  justifyContent: 'center',
  fontFamily: '"Segoe UI", Roboto, Helvetica, Arial, sans-serif',
};

const cardStyle = {
  backgroundColor: '#ffffff',
  maxWidth: '900px',
  width: '100%',
  padding: '30px 20px',
  borderRadius: '12px',
  color: '#333',
  lineHeight: '1.7',
};

const headerStyle = {
  borderBottom: '3px solid #001665',
  marginBottom: '30px',
  paddingBottom: '15px',
};

const titleStyle = {
  color: '#001665',
  fontSize: '1.8rem',
  margin: '0 0 10px 0',
};

// ===== Consent screen reproduction styles (NDS spec colors) =====

const tabletFrameStyle = {
  backgroundColor: '#f7f8fc',
  border: '2px solid #c5cae9',
  borderRadius: '16px',
  padding: '24px 20px',
  maxWidth: '560px',
  margin: '20px auto',
};

const lockRowStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  marginBottom: '18px',
};

const lockIconStyle = {
  fontSize: '20px',
};

const lockTextStyle = {
  color: '#0a1128',
  fontWeight: '600',
  fontSize: '0.95rem',
};

// Required card shown in its accepted (green) state
const consentCardStyle = {
  border: '2px solid #4caf50',
  backgroundColor: '#e8f5e9',
  borderRadius: '12px',
  marginBottom: '16px',
  overflow: 'hidden',
};

// Optional card shown in its default (not accepted) state
const consentCardOptionalStyle = {
  border: '2px solid #9fa8da',
  backgroundColor: '#ffffff',
  borderRadius: '12px',
  marginBottom: '16px',
  overflow: 'hidden',
};

const consentHeaderStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '14px 16px',
  borderBottom: '1px solid rgba(0,0,0,0.06)',
};

const cardIconStyle = {
  fontSize: '20px',
};

const consentTitleStyle = {
  flex: 1,
  color: '#0a1128',
  fontWeight: '700',
  fontSize: '1.05rem',
};

const chevronStyle = {
  color: '#7986cb',
  fontSize: '18px',
};

const consentBodyStyle = {
  padding: '14px 16px 16px',
};

const consentSubheadStyle = {
  color: '#0a1128',
  fontWeight: '700',
  margin: '0 0 8px 0',
  fontSize: '0.95rem',
};

const consentTextStyle = {
  color: '#333',
  fontSize: '0.92rem',
  margin: '0 0 10px 0',
  lineHeight: '1.55',
};

const checkboxRowStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '10px',
  marginTop: '8px',
};

const checkboxCheckedStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '22px',
  height: '22px',
  minWidth: '22px',
  borderRadius: '5px',
  backgroundColor: '#4caf50',
  color: '#ffffff',
  fontWeight: '700',
  fontSize: '14px',
  marginTop: '2px',
};

const checkboxEmptyStyle = {
  display: 'inline-block',
  width: '22px',
  height: '22px',
  minWidth: '22px',
  borderRadius: '5px',
  border: '2px solid #9fa8da',
  backgroundColor: '#ffffff',
  marginTop: '2px',
};

const checkboxLabelStyle = {
  color: '#0a1128',
  fontSize: '0.9rem',
  fontWeight: '600',
  lineHeight: '1.5',
};

const optionalNoteStyle = {
  color: '#777',
  fontSize: '0.82rem',
  fontStyle: 'italic',
  margin: '10px 0 0 0',
};

const acceptButtonStyle = {
  backgroundColor: '#1a237e',
  color: '#ffffff',
  textAlign: 'center',
  fontWeight: '700',
  letterSpacing: '0.5px',
  borderRadius: '10px',
  padding: '14px',
  marginTop: '6px',
};

const smsBubbleStyle = {
  backgroundColor: '#e8eaf6',
  border: '1px solid #c5cae9',
  borderRadius: '14px',
  padding: '12px 16px',
  maxWidth: '440px',
  fontSize: '0.92rem',
  color: '#0a1128',
  marginBottom: '15px',
};

export default SmsOptIn;
